import { randomUUID } from 'crypto';
import { UIBlock } from './uiblock';

/**
 * Thread represents a conversation thread containing multiple UIBlocks
 * Each UIBlock in a thread represents a user question and assistant response pair
 */
export class Thread {
  private id: string;
  private uiblocks: Map<string, UIBlock>;
  private createdAt: Date;

  /**
   * Creates a new Thread instance
   * @param id - Optional custom ID, generates UUID if not provided
   */
  constructor(id?: string) {
    this.id = id || randomUUID();
    this.uiblocks = new Map();
    this.createdAt = new Date();
  }

  /**
   * Get the thread ID
   */
  getId(): string {
    return this.id;
  }

  /**
   * Add a UIBlock to the thread
   */
  addUIBlock(uiblock: UIBlock): void {
    this.uiblocks.set(uiblock.getId(), uiblock);
  }

  /**
   * Get a UIBlock by ID
   */
  getUIBlock(id: string): UIBlock | undefined {
    return this.uiblocks.get(id);
  }

  /**
   * Get all UIBlocks in the thread
   */
  getUIBlocks(): UIBlock[] {
    return Array.from(this.uiblocks.values());
  }

  /**
   * Get UIBlocks as a Map
   */
  getUIBlocksMap(): Map<string, UIBlock> {
    return new Map(this.uiblocks);
  }

  /**
   * Remove a UIBlock by ID
   */
  removeUIBlock(id: string): boolean {
    return this.uiblocks.delete(id);
  }

  /**
   * Check if UIBlock exists
   */
  hasUIBlock(id: string): boolean {
    return this.uiblocks.has(id);
  }

  /**
   * Get number of UIBlocks in the thread
   */
  getUIBlockCount(): number {
    return this.uiblocks.size;
  }

  /**
   * Clear all UIBlocks from the thread
   */
  clear(): void {
    this.uiblocks.clear();
  }

  /**
   * Get creation timestamp
   */
  getCreatedAt(): Date {
    return this.createdAt;
  }

  /**
   * Get conversation context from recent UIBlocks (excluding current one)
   * Returns formatted string with previous questions and component summaries
   * @param limit - Maximum number of previous UIBlocks to include (default: 5)
   * @param currentUIBlockId - ID of current UIBlock to exclude from context (optional)
   * @returns Formatted conversation history string
   */
  getConversationContext(limit: number = 5, currentUIBlockId?: string): string {
    if (limit === 0) {
      return '';
    }

    // Get all UIBlocks sorted by creation time (oldest first)
    const allBlocks = Array.from(this.uiblocks.values())
      .filter(block => !currentUIBlockId || block.getId() !== currentUIBlockId)
      .sort((a, b) => a.getCreatedAt().getTime() - b.getCreatedAt().getTime());

    if (allBlocks.length === 0) {
      return '';
    }

    // Take the last N blocks (most recent)
    const recentBlocks = allBlocks.slice(-limit);

    // Format as conversation history
    const contextLines: string[] = [];

    recentBlocks.forEach((block, index) => {
      const questionNum = index + 1;
      const question = block.getUserQuestion();
      const metadata = block.getComponentMetadata();
      const textResponse = block.getTextResponse();

      // Determine what was generated and build appropriate response
      let assistantResponse = '';

      // Check if component was generated (metadata exists and has meaningful content)
      const hasComponent = metadata && Object.keys(metadata).length > 0 && metadata.type;

      // Check if text response was generated
      const hasTextResponse = textResponse && textResponse.trim().length > 0;

      const responseParts: string[] = [];

      if (hasComponent) {
        // Component was generated - show brief summary only.
        // Do NOT include full props JSON — it contains SQL queries, queryIds,
        // component configs, and layout data that bloats conversation history
        // by ~13K tokens per previous question. The main agent only needs to
        // know what was asked and what type of answer was generated.
        const parts: string[] = [];

        if (metadata.type) {
          parts.push(`Component Type: ${metadata.type}`);
        }
        if (metadata.name) {
          parts.push(`Name: ${metadata.name}`);
        }
        if (metadata.description) {
          parts.push(`Description: ${metadata.description}`);
        }

        // Extract only the layout title from props (if available) — not the full JSON
        const layoutTitle = metadata.props?.config?.title;
        if (layoutTitle) {
          parts.push(`Dashboard Title: ${layoutTitle}`);
        }

        responseParts.push(parts.join('\n'));
      }

      if (hasTextResponse) {
        // Text response was generated - add a trimmed version.
        // The full text can contain source agent internals (SQL, schema searches,
        // data previews, stream markers) which bloat conversation history.
        // Truncate to first 500 chars — enough for the main agent to understand
        // what was previously discussed without re-sending full analysis.
        const trimmedText = textResponse.length > 500
          ? textResponse.substring(0, 500) + '...[truncated]'
          : textResponse;
        responseParts.push(trimmedText);
      }

      if (responseParts.length > 0) {
        // Join both component and text response if both exist
        assistantResponse = responseParts.join('\n');
      } else {
        // Nothing was generated
        assistantResponse = 'No response generated';
      }

      contextLines.push(`User:\n ${question}`);
      contextLines.push(`Assistant:\n ${assistantResponse}`);
      contextLines.push('---'); // Empty line for readability
    });

    return contextLines.join('\n').trim();
  }

  /**
   * Convert Thread to JSON-serializable object
   */
  toJSON(): Record<string, any> {
    return {
      id: this.id,
      uiblocks: Array.from(this.uiblocks.values()).map(block => block.toJSON()),
      createdAt: this.createdAt.toISOString(),
    };
  }
}
